import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import { TaskScheduleApiProvider, useTaskScheduleApi } from '../Services/TaskScheduleApiProvider';
import { TaskScheduleCreateApi } from '../Services/TaskScheduleApiProviderTypes';
import TaskScheduleForm from './TaskScheduleForm';
import {
  TaskScheduleForm as FormState,
  INITIAL_FORM_STATE,
  mapFormToApi,
  validate,
} from './TaskScheduleHelpers';

function NewTaskScheduleContent() {
  const navigate = useNavigate();

  const { organizationId } = useParams();
  const { showAlert } = useAlert();

  const { createTaskSchedule, getAssistants } = useTaskScheduleApi();

  const [loading, setLoading] = useState(false);

  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        setLoading(true);
        const assistants = await getAssistants();
        setAgents(assistants);
      } catch (error) {
        showAlert('fail', 'Failed to fetch assistants');
      } finally {
        setLoading(false);
      }
    };

    fetchAssistants();
  }, []);

  const onCreate = async () => {
    if (!validate(form, showAlert)) return;

    try {
      setLoading(true);

      const formData: TaskScheduleCreateApi = mapFormToApi(form);
      await createTaskSchedule(formData);

      showAlert('success', 'Task schedule created successfully!');
      navigate(`/organizations/${organizationId}/task-schedule`);
    } catch (error) {
      showAlert('fail', 'Failed to create task schedule');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/task-schedule`);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>
              <TaskScheduleForm form={form} agents={agents} onChange={setForm} />
            </div>
            <div className="buttons-container justify-end gap-12">
              <Button background="color2" size="medium" onClick={onCreate}>
                Create
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewTaskSchedule() {
  return (
    <TaskScheduleApiProvider>
      <NewTaskScheduleContent />
    </TaskScheduleApiProvider>
  );
}

export default NewTaskSchedule;
